import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  Download,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Leaf,
  Sprout,
  Bug,
  Tractor,
  DollarSign,
  List,
  Sparkles,
  TreeDeciduous,
  Wheat,
  Bird,
  PenTool,
  Clock,
  Lightbulb,
  Droplets
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { agricultureCurriculum } from '@/data/agricultureCurriculum';
import { 
  year11AgricultureUnitTests,
  year12AgricultureUnitTests,
  year13AgricultureUnitTests
} from '@/data/agricultureUnitTests';
import { 
  year11AgricultureStudentNotes,
  year12AgricultureStudentNotes,
  year13AgricultureStudentNotes
} from '@/data/agricultureStudentNotes';
import { agricultureWeeklyLessons } from '@/data/agricultureWeeklyLessons';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files } from 'lucide-react';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';
import { useLanguage } from '@/contexts/LanguageContext';

export default function AgricultureCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const { isFrench, t } = useLanguage();

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        // Support both Junior (7-10) and Senior (11-13) Agriculture
        const years = [...new Set(classes
          .filter(c => c.year >= 7 && c.year <= 13 && 
            (c.subject?.toLowerCase().includes('agriculture') || 
             c.subject?.toLowerCase().includes('agri') ||
             c.name?.toLowerCase().includes('agriculture')))
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        // Check department field for specific agriculture year assignments
        // e.g., "Agriculture 10e" or "Agriculture Year 10" or "Junior Agriculture"
        const dept = currentUser?.department?.toLowerCase() || '';
        let deptYears = [];
        
        // Match patterns like "Agriculture 10e", "Agriculture 7e", "agriculture year 10"
        const agriYearMatches = dept.matchAll(/agriculture\s*(\d+)e?|agriculture\s*year\s*(\d+)/gi);
        for (const match of agriYearMatches) {
          const year = match[1] || match[2];
          if (year) deptYears.push(`Year ${year}`);
        }
        
        // If no specific year found, check for "junior agriculture" pattern
        const isJuniorAgri = dept.includes('junior') && dept.includes('agri');
        const defaultYears = isJuniorAgri 
          ? ["Year 7", "Year 8", "Year 9", "Year 10"]
          : ["Year 11", "Year 12", "Year 13"];
        
        // Priority: API classes > department years > default
        const finalYears = years.length > 0 ? years : (deptYears.length > 0 ? deptYears : defaultYears);
        
        setTeacherYears(finalYears);
        if (finalYears.length > 0) {
          setSelectedYear(finalYears[0]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        // Fallback: parse department for agriculture year
        const dept = currentUser?.department?.toLowerCase() || '';
        let deptYears = [];
        
        const agriYearMatches = dept.matchAll(/agriculture\s*(\d+)e?|agriculture\s*year\s*(\d+)/gi);
        for (const match of agriYearMatches) {
          const year = match[1] || match[2];
          if (year) deptYears.push(`Year ${year}`);
        }
        
        const isJuniorAgri = dept.includes('junior') && dept.includes('agri');
        const defaultYears = isJuniorAgri 
          ? ["Year 7", "Year 8", "Year 9", "Year 10"] 
          : ["Year 11", "Year 12", "Year 13"];
        
        setTeacherYears(deptYears.length > 0 ? deptYears : defaultYears);
        if (deptYears.length > 0) {
          setSelectedYear(deptYears[0]);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  const strandIcons = {
    0: TreeDeciduous, // Sustainable Farm Ecosystem
    1: Sprout,        // Soil Management
    2: Wheat,         // Plant and Animal Production
    3: Bug,           // Crop and Animal Protection
    4: DollarSign     // Farm Management and Economics
  };

  const strandColors = ["green", "amber", "blue", "red", "purple"];

  // Check if selected year is Junior (7-10)
  const isJuniorYear = () => {
    const year = parseInt(selectedYear.replace('Year ', ''));
    return year >= 7 && year <= 10;
  };

  // Get curriculum data for selected year
  const getCurriculumData = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    return agricultureCurriculum[yearKey] || agricultureCurriculum.year11;
  };

  // Get unit tests for selected year
  const getUnitTests = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const testsMap = {
      'year11': year11AgricultureUnitTests,
      'year12': year12AgricultureUnitTests,
      'year13': year13AgricultureUnitTests
    };
    return testsMap[yearKey] || year11AgricultureUnitTests;
  };

  // Get unit tests for year (formatted for TermExamGenerator)
  const getUnitTestsForYear = () => {
    const tests = getUnitTests();
    const allTests = [];
    ['term1', 'term2', 'term3', 'term4'].forEach(term => {
      if (tests?.[term]?.tests) {
        allTests.push(...tests[term].tests);
      }
    });
    return allTests;
  };

  // Get student notes for selected year
  const getStudentNotes = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const notesMap = {
      'year11': year11AgricultureStudentNotes,
      'year12': year12AgricultureStudentNotes,
      'year13': year13AgricultureStudentNotes
    };
    return notesMap[yearKey] || year11AgricultureStudentNotes;
  };

  const curriculumData = getCurriculumData();
  const unitTests = getUnitTests();
  const studentNotes = getStudentNotes();

  // Junior Agriculture Curriculum Data (Years 7-10) with SLOs
  const juniorAgricultureCurriculum = {
    year7: {
      strand: "Introduction to Agriculture",
      topics: [
        {
          name: "Introduction to Agriculture",
          slos: [
            "Explain what agriculture is and its importance to society",
            "Identify different types of agricultural systems (crop farming, livestock farming)",
            "Describe the role of agriculture in providing food, fiber, and other products"
          ],
          activities: ["Discussions on agriculture's impact", "Research presentations", "Farm visits"]
        },
        {
          name: "Plant Science Basics",
          slos: [
            "Identify the basic parts of a plant and their functions",
            "Describe the process of photosynthesis",
            "Explain the basic needs of plants (water, light, nutrients, air)"
          ],
          activities: ["Plant dissection", "Photosynthesis experiments", "Growing plants in class"]
        },
        {
          name: "Soil Science",
          slos: [
            "Describe what soil is and its importance for plant growth",
            "Identify different soil types and their characteristics",
            "Explain the role of soil in the ecosystem"
          ],
          activities: ["Soil sampling and analysis", "Creating soil profiles", "Soil texture tests"]
        },
        {
          name: "Animal Science Basics",
          slos: [
            "Identify common farm animals and their basic characteristics",
            "Describe the basic needs of animals (food, water, shelter, care)",
            "Explain how animals are used in agriculture"
          ],
          activities: ["Research on farm animals", "Creating animal care plans", "Farm animal observation"]
        },
        {
          name: "Sustainable Farming Practices",
          slos: [
            "Define sustainability in agriculture",
            "Identify basic sustainable farming practices"
          ],
          activities: ["Discussions on environmental impact", "Case studies of sustainable farms"]
        },
        {
          name: "Safety in Agriculture",
          slos: [
            "Identify common safety hazards on farms",
            "Demonstrate safe practices when working with plants and animals"
          ],
          activities: ["Farm safety audits", "Practical demonstrations of safe procedures"]
        }
      ]
    },
    year8: {
      strand: "Plant Growth and Development",
      topics: [
        {
          name: "Plant Growth and Development",
          slos: [
            "Describe the plant life cycle",
            "Explain factors affecting plant growth (temperature, pH, nutrients)",
            "Identify common plant diseases and pests"
          ],
          activities: ["Observing plant growth stages", "Investigating environmental factors"]
        },
        {
          name: "Crop Production",
          slos: [
            "Describe different methods of crop production",
            "Explain the importance of crop rotation",
            "Identify major crops grown in the region"
          ],
          activities: ["Field trips to farms", "Research on crop cultivation", "Crop planning exercises"]
        },
        {
          name: "Soil Management",
          slos: [
            "Explain soil composition and structure",
            "Describe methods of soil testing",
            "Identify ways to improve soil fertility"
          ],
          activities: ["Soil testing practicals", "Creating compost", "Soil amendment experiments"]
        },
        {
          name: "Animal Husbandry",
          slos: [
            "Describe breeding and reproduction in farm animals",
            "Explain animal nutrition and feeding requirements",
            "Identify common animal diseases and preventive measures"
          ],
          activities: ["Guest speakers from livestock industry", "Researching animal health protocols"]
        },
        {
          name: "Agricultural Technology",
          slos: [
            "Identify basic farm equipment and tools",
            "Explain how technology is used in modern farming"
          ],
          activities: ["Demonstrations of farm machinery", "Research on precision agriculture"]
        },
        {
          name: "Water Management",
          slos: [
            "Describe the importance of water in agriculture",
            "Identify different irrigation methods",
            "Explain water conservation techniques"
          ],
          activities: ["Designing irrigation systems", "Calculating water needs"]
        }
      ]
    },
    year9: {
      strand: "Advanced Plant Science",
      topics: [
        {
          name: "Advanced Plant Science",
          slos: [
            "Explain plant genetics and breeding",
            "Describe plant propagation methods (seeds, cuttings, grafting)",
            "Analyze factors affecting crop yield and quality"
          ],
          activities: ["Practicing propagation techniques", "Genetic studies", "Grafting practice"]
        },
        {
          name: "Pest and Disease Management",
          slos: [
            "Identify integrated pest management (IPM) strategies",
            "Explain the use of pesticides and their safety considerations",
            "Describe biological control methods"
          ],
          activities: ["Developing IPM plans", "Researching natural pesticides"]
        },
        {
          name: "Advanced Soil Science",
          slos: [
            "Analyze soil nutrients and their role in plant growth",
            "Explain soil erosion and conservation methods",
            "Describe composting and organic matter management"
          ],
          activities: ["Advanced soil testing", "Designing erosion control measures"]
        },
        {
          name: "Livestock Production",
          slos: [
            "Explain different livestock production systems",
            "Describe animal welfare principles",
            "Analyze factors affecting animal productivity"
          ],
          activities: ["Case studies of livestock operations", "Debates on animal welfare"]
        },
        {
          name: "Agricultural Economics",
          slos: [
            "Explain basic economic principles in agriculture",
            "Describe farm management and record keeping",
            "Identify costs and benefits in agricultural production"
          ],
          activities: ["Creating farm budgets", "Analyzing market trends"]
        },
        {
          name: "Sustainable Agriculture",
          slos: [
            "Analyze environmental impacts of farming practices",
            "Describe organic farming principles",
            "Explain climate change and its effects on agriculture"
          ],
          activities: ["Environmental impact assessments", "Exploring organic certifications"]
        }
      ]
    },
    year10: {
      strand: "Agricultural Systems",
      topics: [
        {
          name: "Agricultural Systems",
          slos: [
            "Compare and contrast different agricultural systems globally",
            "Analyze the efficiency of various farming systems",
            "Evaluate the sustainability of different agricultural practices"
          ],
          activities: ["Global agricultural system comparisons", "Efficiency analysis projects"]
        },
        {
          name: "Biotechnology in Agriculture",
          slos: [
            "Explain the role of biotechnology in modern agriculture",
            "Describe genetic modification and its applications",
            "Analyze ethical considerations in agricultural biotechnology"
          ],
          activities: ["Debates on GMOs", "Researching biotechnological advancements"]
        },
        {
          name: "Advanced Crop Management",
          slos: [
            "Design crop production plans",
            "Analyze harvest and post-harvest management",
            "Evaluate crop quality and grading systems"
          ],
          activities: ["Developing detailed crop plans", "Simulating harvest logistics"]
        },
        {
          name: "Advanced Animal Production",
          slos: [
            "Analyze breeding programs and selection criteria",
            "Evaluate animal health management systems",
            "Describe value-added animal products"
          ],
          activities: ["Designing breeding strategies", "Analyzing animal health records"]
        },
        {
          name: "Farm Business Management",
          slos: [
            "Develop farm business plans",
            "Analyze farm financial records and budgets",
            "Evaluate marketing strategies for agricultural products"
          ],
          activities: ["Creating comprehensive business plans", "Simulating farm financial management"]
        },
        {
          name: "Food Security and Global Agriculture",
          slos: [
            "Explain global food security challenges",
            "Analyze the relationship between agriculture and nutrition",
            "Evaluate solutions to food security issues"
          ],
          activities: ["Researching global food production", "Proposing solutions to food insecurity"]
        },
        {
          name: "Career Pathways in Agriculture",
          slos: [
            "Identify career opportunities in agriculture",
            "Describe qualifications and skills needed for agricultural careers",
            "Evaluate pathways for further study in agriculture"
          ],
          activities: ["Guest speakers from various agricultural professions", "Career research projects"]
        }
      ]
    }
  };

  // French Junior Agriculture Curriculum Data (Years 7-10) - For College de Tagaga
  const juniorAgricultureCurriculumFrench = {
    year7: {
      strand: "Introduction à l'Agriculture",
      topics: [
        {
          name: "Introduction à l'Agriculture",
          slos: [
            "Identifier différents types d'agriculture et leur importance pour la société",
            "Expliquer le rôle de l'agriculture dans la production alimentaire et l'économie"
          ],
          activities: [
            "Créer une carte visuelle des types d'agriculture",
            "Visiter des fermes locales",
            "Rechercher et présenter sur l'agriculture dans différentes régions"
          ]
        },
        {
          name: "Bases de la Science des Plantes",
          slos: [
            "Décrire les structures végétales de base et leurs fonctions",
            "Identifier différents types de plantes utilisées en agriculture",
            "Comprendre le cycle de vie des plantes"
          ],
          activities: [
            "Effectuer une dissection de plante",
            "Créer des diagrammes de structures végétales",
            "Planter des graines et observer la croissance"
          ]
        },
        {
          name: "Science du Sol",
          slos: [
            "Décrire la composition de base du sol",
            "Identifier différents types de sol et leurs caractéristiques",
            "Expliquer l'importance du sol pour la croissance des plantes"
          ],
          activities: [
            "Effectuer le test du bocal de sol",
            "Comparer différents échantillons de sol",
            "Expérience de croissance de plantes dans différents sols"
          ]
        },
        {
          name: "Bases de la Science Animale",
          slos: [
            "Identifier les animaux de ferme courants et leurs utilisations",
            "Décrire les besoins de base des animaux (nourriture, eau, abri)",
            "Comprendre le comportement animal de base"
          ],
          activities: [
            "Visite de ferme pour observer les animaux",
            "Créer un guide des animaux de ferme",
            "Concevoir un logement idéal pour animaux"
          ]
        },
        {
          name: "Agriculture Durable",
          slos: [
            "Définir la durabilité en agriculture",
            "Identifier les pratiques agricoles durables",
            "Expliquer l'importance de la gestion environnementale"
          ],
          activities: [
            "Rechercher l'agriculture durable",
            "Concevoir une ferme durable",
            "Débattre des pratiques durables vs conventionnelles"
          ]
        },
        {
          name: "Sécurité en Agriculture",
          slos: [
            "Identifier les dangers courants dans les fermes",
            "Démontrer la manipulation sécuritaire des outils et équipements",
            "Comprendre l'équipement de protection individuelle (EPI)"
          ],
          activities: [
            "Création d'affiche de sécurité",
            "Démonstration pratique d'utilisation des outils",
            "Démonstration d'EPI"
          ]
        }
      ]
    },
    year8: {
      strand: "Croissance et Développement des Plantes",
      topics: [
        {
          name: "Croissance et Développement des Plantes",
          slos: [
            "Expliquer le processus de photosynthèse",
            "Décrire les facteurs affectant la croissance des plantes",
            "Comprendre les méthodes de reproduction des plantes"
          ],
          activities: [
            "Expérience du disque foliaire",
            "Expérience de croissance de plantes",
            "Observation de la pollinisation"
          ]
        },
        {
          name: "Production de Cultures",
          slos: [
            "Planifier et préparer un calendrier de plantation",
            "Démontrer les techniques de plantation appropriées",
            "Identifier et contrôler les mauvaises herbes courantes"
          ],
          activities: [
            "Développer un calendrier de plantation",
            "Activité de plantation pratique",
            "Promenade d'identification des mauvaises herbes"
          ]
        },
        {
          name: "Gestion du Sol",
          slos: [
            "Tester le pH du sol et les niveaux de nutriments",
            "Appliquer les engrais et amendements du sol appropriés",
            "Comprendre le compostage et la matière organique"
          ],
          activities: [
            "Laboratoire de test de sol",
            "Exercice de calcul d'engrais",
            "Construire un bac à compost"
          ]
        },
        {
          name: "Élevage",
          slos: [
            "Identifier différentes races d'animaux et leurs caractéristiques",
            "Calculer les rations alimentaires pour les animaux",
            "Démontrer les techniques de base de manipulation des animaux"
          ],
          activities: [
            "Projet de comparaison de races",
            "Calcul de ration alimentaire",
            "Pratique de manipulation d'animaux"
          ]
        },
        {
          name: "Technologie Agricole",
          slos: [
            "Identifier et utiliser les outils et machines agricoles courants",
            "Comprendre l'entretien de base des machines",
            "Explorer les technologies d'agriculture de précision"
          ],
          activities: [
            "Quiz d'identification d'outils",
            "Atelier d'entretien de machines",
            "Démonstration GPS et drone"
          ]
        },
        {
          name: "Gestion de l'Eau",
          slos: [
            "Expliquer le cycle de l'eau et son importance pour l'agriculture",
            "Identifier les méthodes d'irrigation et leurs applications",
            "Comprendre les pratiques de conservation de l'eau"
          ],
          activities: [
            "Diagramme du cycle de l'eau",
            "Construire un modèle d'irrigation",
            "Concevoir un système de collecte d'eau de pluie"
          ]
        }
      ]
    },
    year9: {
      strand: "Science des Plantes Avancée",
      topics: [
        {
          name: "Science des Plantes Avancée",
          slos: [
            "Comprendre la génétique et la sélection des plantes",
            "Expliquer les processus de pollinisation et de fertilisation",
            "Démontrer les techniques de greffage et de propagation"
          ],
          activities: [
            "Exercices de carré de Punnett",
            "Pratique de pollinisation manuelle",
            "Démonstration de greffage"
          ]
        },
        {
          name: "Gestion des Ravageurs et Maladies",
          slos: [
            "Identifier les ravageurs et maladies courants",
            "Appliquer les principes de Lutte Intégrée contre les Ravageurs (LIR)",
            "Évaluer les méthodes de contrôle chimique et biologique"
          ],
          activities: [
            "Activité de surveillance des ravageurs",
            "Développer un plan de LIR",
            "Débat sur l'utilisation de pesticides"
          ]
        },
        {
          name: "Science du Sol Avancée",
          slos: [
            "Analyser la structure du sol et son impact sur l'agriculture",
            "Comprendre les micro-organismes du sol et leurs rôles",
            "Mettre en œuvre des stratégies de prévention de l'érosion du sol"
          ],
          activities: [
            "Analyse de la texture du sol",
            "Travail au microscope",
            "Simulation d'érosion"
          ]
        },
        {
          name: "Production Animale",
          slos: [
            "Planifier un système de production animale",
            "Comprendre la nutrition animale et la gestion de la santé",
            "Appliquer les normes de bien-être animal"
          ],
          activities: [
            "Concevoir un système d'élevage",
            "Créer un plan nutritionnel",
            "Audit du bien-être animal"
          ]
        },
        {
          name: "Économie Agricole",
          slos: [
            "Analyser l'offre et la demande sur les marchés agricoles",
            "Calculer les coûts de production et les marges bénéficiaires",
            "Comprendre les stratégies de marketing agricole"
          ],
          activities: [
            "Jeu de simulation de marché",
            "Analyse coûts-avantages",
            "Développement de plan marketing"
          ]
        },
        {
          name: "Agriculture Durable",
          slos: [
            "Évaluer les systèmes d'agriculture biologique et conventionnelle",
            "Concevoir un système de permaculture",
            "Évaluer l'impact environnemental des pratiques agricoles"
          ],
          activities: [
            "Comparer les systèmes agricoles",
            "Concevoir un jardin en permaculture",
            "Évaluation d'impact environnemental"
          ]
        }
      ]
    },
    year10: {
      strand: "Systèmes Agricoles",
      topics: [
        {
          name: "Systèmes Agricoles",
          slos: [
            "Analyser différents systèmes de production agricole (intensif, extensif, biologique)",
            "Évaluer l'efficacité et la durabilité des systèmes agricoles",
            "Comprendre le concept d'agroécologie"
          ],
          activities: [
            "Projet de comparaison de systèmes",
            "Audit de durabilité",
            "Étude de cas en agroécologie"
          ]
        },
        {
          name: "Biotechnologie en Agriculture",
          slos: [
            "Expliquer la modification génétique et ses applications en agriculture",
            "Comprendre les techniques de culture tissulaire et de clonage",
            "Débattre des implications éthiques de la biotechnologie agricole"
          ],
          activities: [
            "Débat sur les OGM",
            "Observation de culture tissulaire",
            "Discussion éthique"
          ]
        },
        {
          name: "Gestion Avancée des Cultures",
          slos: [
            "Concevoir et gérer un système de production en serre",
            "Mettre en œuvre des stratégies de rotation et d'intercalaire des cultures",
            "Analyser les données de rendement des cultures et apporter des améliorations"
          ],
          activities: [
            "Projet de conception de serre",
            "Planification de rotation des cultures",
            "Analyse des données de rendement"
          ]
        },
        {
          name: "Gestion Avancée des Animaux",
          slos: [
            "Développer des programmes complets de santé animale",
            "Comprendre la génétique animale et les programmes de sélection",
            "Évaluer différents systèmes de logement et de gestion"
          ],
          activities: [
            "Développement de programme de santé",
            "Projet d'analyse génétique",
            "Évaluation de système"
          ]
        },
        {
          name: "Gestion d'Entreprise Agricole",
          slos: [
            "Développer un plan d'affaires agricole complet",
            "Analyser les états financiers et les budgets",
            "Comprendre la gestion des risques et l'assurance"
          ],
          activities: [
            "Création de plan d'affaires",
            "Exercice d'analyse financière",
            "Activité d'évaluation des risques"
          ]
        },
        {
          name: "Sécurité Alimentaire et Avenir",
          slos: [
            "Examiner les défis de la sécurité alimentaire mondiale",
            "Explorer les impacts du changement climatique sur l'agriculture",
            "Enquêter sur les parcours de carrière en agriculture"
          ],
          activities: [
            "Recherche sur la sécurité alimentaire mondiale",
            "Présentation sur l'impact climatique",
            "Exploration des parcours de carrière"
          ]
        }
      ]
    }
  };

  // Get Junior curriculum data for selected year (with French support)
  const getJuniorCurriculumData = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    // Use French curriculum for French schools (Tagaga)
    if (isFrench) {
      return juniorAgricultureCurriculumFrench[yearKey] || juniorAgricultureCurriculumFrench.year7;
    }
    return juniorAgricultureCurriculum[yearKey] || juniorAgricultureCurriculum.year7;
  };

  // Get topic color based on index
  const getTopicColor = (index) => {
    const colors = [
      "border-l-green-500",
      "border-l-amber-500",
      "border-l-blue-500",
      "border-l-purple-500",
      "border-l-rose-500",
      "border-l-cyan-500",
      "border-l-orange-500"
    ];
    return colors[index % colors.length];
  };

  // Get topic icon based on index
  const getTopicIcon = (index) => {
    const icons = [
      <Leaf key="leaf" className="h-5 w-5 text-green-600" />,
      <Sprout key="sprout" className="h-5 w-5 text-amber-600" />,
      <TreeDeciduous key="tree" className="h-5 w-5 text-blue-600" />,
      <Bird key="bird" className="h-5 w-5 text-purple-600" />,
      <Bug key="bug" className="h-5 w-5 text-rose-600" />,
      <Droplets key="droplets" className="h-5 w-5 text-cyan-600" />,
      <DollarSign key="dollar" className="h-5 w-5 text-orange-600" />
    ];
    return icons[index % icons.length];
  };

  // Junior Agriculture Weekly Lessons for Scheme of Work component
  const juniorAgricultureWeeklyLessons = {
    year7: {
      term1: {
        weeks: [
          { week: 1, strand: "Introduction to Agriculture", topic: "What is Agriculture?", learningOutcomes: ["Explain what agriculture is and its importance to society", "Identify different types of agricultural systems"], activities: ["Discussions on agriculture's impact", "Research presentations"], resources: ["Textbook", "Farm images"], assessment: "Introduction quiz" },
          { week: 2, strand: "Introduction to Agriculture", topic: "Types of Agriculture", learningOutcomes: ["Identify crop farming, livestock farming", "Describe role of agriculture in providing food"], activities: ["Farm visits", "Poster creation"], resources: ["Visual aids"], assessment: "Type identification test" },
          { week: 3, strand: "Plant Science Basics", topic: "Parts of a Plant", learningOutcomes: ["Identify the basic parts of a plant", "Describe plant functions"], activities: ["Plant dissection", "Labeling exercises"], resources: ["Plant specimens"], assessment: "Plant parts quiz" },
          { week: 4, strand: "Plant Science Basics", topic: "Photosynthesis", learningOutcomes: ["Describe the process of photosynthesis", "Explain plant needs"], activities: ["Photosynthesis experiments", "Growing plants"], resources: ["Lab equipment"], assessment: "Photosynthesis test" },
          { week: 5, strand: "Soil Science", topic: "Introduction to Soil", learningOutcomes: ["Describe what soil is", "Explain importance for plant growth"], activities: ["Soil sampling", "Soil profiles"], resources: ["Soil samples"], assessment: "Soil basics quiz" },
          { week: 6, strand: "Soil Science", topic: "Soil Types", learningOutcomes: ["Identify different soil types", "Describe soil characteristics"], activities: ["Soil texture tests", "Classification"], resources: ["Different soils"], assessment: "Soil types test" },
          { week: 7, strand: "Animal Science Basics", topic: "Farm Animals", learningOutcomes: ["Identify common farm animals", "Describe basic animal needs"], activities: ["Research on animals", "Animal care plans"], resources: ["Animal images"], assessment: "Animal ID quiz" },
          { week: 8, strand: "Animal Science Basics", topic: "Animal Uses", learningOutcomes: ["Explain how animals are used in agriculture", "Describe animal products"], activities: ["Farm observation", "Product analysis"], resources: ["Farm visit"], assessment: "Animal uses test" },
          { week: 9, strand: "Sustainable Farming", topic: "Sustainability Basics", learningOutcomes: ["Define sustainability in agriculture", "Identify sustainable practices"], activities: ["Discussions", "Case studies"], resources: ["Sustainability guides"], assessment: "Sustainability quiz" },
          { week: 10, strand: "Safety", topic: "Farm Safety", learningOutcomes: ["Identify common safety hazards", "Demonstrate safe practices"], activities: ["Safety audits", "Safe procedure demos"], resources: ["Safety equipment"], assessment: "Safety test" }
        ]
      },
      term2: { weeks: [] },
      term3: { weeks: [] },
      term4: { weeks: [] }
    },
    year8: {
      term1: {
        weeks: [
          { week: 1, strand: "Plant Growth", topic: "Plant Life Cycle", learningOutcomes: ["Describe the plant life cycle", "Identify growth stages"], activities: ["Observing growth stages", "Life cycle diagrams"], resources: ["Seeds", "Growth charts"], assessment: "Life cycle quiz" },
          { week: 2, strand: "Plant Growth", topic: "Factors Affecting Growth", learningOutcomes: ["Explain factors affecting plant growth", "Investigate environmental factors"], activities: ["Growth experiments", "Data collection"], resources: ["Lab materials"], assessment: "Factors test" },
          { week: 3, strand: "Crop Production", topic: "Crop Production Methods", learningOutcomes: ["Describe different methods of crop production", "Explain crop rotation"], activities: ["Field trips", "Method comparison"], resources: ["Farm visits"], assessment: "Methods quiz" },
          { week: 4, strand: "Crop Production", topic: "Regional Crops", learningOutcomes: ["Identify major crops grown in the region", "Describe local agriculture"], activities: ["Crop research", "Planning exercises"], resources: ["Local crop guides"], assessment: "Regional crops test" },
          { week: 5, strand: "Soil Management", topic: "Soil Composition", learningOutcomes: ["Explain soil composition and structure", "Describe soil testing methods"], activities: ["Soil testing practicals", "Composition analysis"], resources: ["Test kits"], assessment: "Composition quiz" },
          { week: 6, strand: "Soil Management", topic: "Improving Fertility", learningOutcomes: ["Identify ways to improve soil fertility", "Apply improvement methods"], activities: ["Creating compost", "Amendment experiments"], resources: ["Composting materials"], assessment: "Fertility test" },
          { week: 7, strand: "Animal Husbandry", topic: "Animal Breeding", learningOutcomes: ["Describe breeding and reproduction", "Explain animal nutrition"], activities: ["Guest speakers", "Nutrition research"], resources: ["Industry contacts"], assessment: "Breeding quiz" },
          { week: 8, strand: "Animal Husbandry", topic: "Animal Health", learningOutcomes: ["Identify common animal diseases", "Describe preventive measures"], activities: ["Health protocol research", "Disease ID"], resources: ["Health guides"], assessment: "Health test" },
          { week: 9, strand: "Technology", topic: "Farm Equipment", learningOutcomes: ["Identify basic farm equipment", "Explain technology in farming"], activities: ["Equipment demos", "Technology research"], resources: ["Equipment manuals"], assessment: "Technology quiz" },
          { week: 10, strand: "Water Management", topic: "Irrigation", learningOutcomes: ["Describe water importance", "Identify irrigation methods"], activities: ["Irrigation design", "Water calculations"], resources: ["Irrigation materials"], assessment: "Water management test" }
        ]
      },
      term2: { weeks: [] },
      term3: { weeks: [] },
      term4: { weeks: [] }
    },
    year9: {
      term1: {
        weeks: [
          { week: 1, strand: "Advanced Plant Science", topic: "Plant Genetics", learningOutcomes: ["Explain plant genetics and breeding", "Describe propagation methods"], activities: ["Propagation practice", "Genetic studies"], resources: ["Plant materials"], assessment: "Genetics quiz" },
          { week: 2, strand: "Advanced Plant Science", topic: "Propagation Methods", learningOutcomes: ["Practice seeds, cuttings, grafting", "Analyze crop yield factors"], activities: ["Grafting practice", "Yield analysis"], resources: ["Grafting tools"], assessment: "Propagation test" },
          { week: 3, strand: "Pest Management", topic: "IPM Strategies", learningOutcomes: ["Identify integrated pest management strategies", "Explain pesticide safety"], activities: ["IPM planning", "Safety protocols"], resources: ["IPM guides"], assessment: "IPM quiz" },
          { week: 4, strand: "Pest Management", topic: "Biological Control", learningOutcomes: ["Describe biological control methods", "Apply natural solutions"], activities: ["Natural pesticide research", "Application practice"], resources: ["Control materials"], assessment: "Bio-control test" },
          { week: 5, strand: "Advanced Soil Science", topic: "Soil Nutrients", learningOutcomes: ["Analyze soil nutrients", "Explain role in plant growth"], activities: ["Advanced testing", "Nutrient analysis"], resources: ["Test equipment"], assessment: "Nutrients quiz" },
          { week: 6, strand: "Advanced Soil Science", topic: "Erosion Control", learningOutcomes: ["Explain soil erosion and conservation", "Design control measures"], activities: ["Erosion observation", "Design exercises"], resources: ["Conservation guides"], assessment: "Erosion test" },
          { week: 7, strand: "Livestock Production", topic: "Production Systems", learningOutcomes: ["Explain different livestock systems", "Describe animal welfare"], activities: ["System case studies", "Welfare debates"], resources: ["Production guides"], assessment: "Systems quiz" },
          { week: 8, strand: "Agricultural Economics", topic: "Farm Economics", learningOutcomes: ["Explain basic economic principles", "Describe farm management"], activities: ["Budget creation", "Market analysis"], resources: ["Financial tools"], assessment: "Economics test" },
          { week: 9, strand: "Sustainable Agriculture", topic: "Environmental Impacts", learningOutcomes: ["Analyze environmental impacts", "Describe organic farming"], activities: ["Impact assessments", "Organic exploration"], resources: ["Environmental guides"], assessment: "Sustainability quiz" },
          { week: 10, strand: "Sustainable Agriculture", topic: "Climate Change", learningOutcomes: ["Explain climate change effects on agriculture", "Propose adaptations"], activities: ["Climate research", "Adaptation planning"], resources: ["Climate data"], assessment: "Climate test" }
        ]
      },
      term2: { weeks: [] },
      term3: { weeks: [] },
      term4: { weeks: [] }
    },
    year10: {
      term1: {
        weeks: [
          { week: 1, strand: "Agricultural Systems", topic: "Global Systems", learningOutcomes: ["Compare different agricultural systems globally", "Analyze efficiency"], activities: ["Global comparisons", "Efficiency projects"], resources: ["World agriculture data"], assessment: "Systems quiz" },
          { week: 2, strand: "Agricultural Systems", topic: "Sustainability Evaluation", learningOutcomes: ["Evaluate sustainability of practices", "Propose improvements"], activities: ["Sustainability audits", "Improvement plans"], resources: ["Assessment tools"], assessment: "Evaluation test" },
          { week: 3, strand: "Biotechnology", topic: "Biotech in Agriculture", learningOutcomes: ["Explain biotechnology role", "Describe genetic modification"], activities: ["GMO debates", "Research projects"], resources: ["Biotech guides"], assessment: "Biotech quiz" },
          { week: 4, strand: "Biotechnology", topic: "Ethical Considerations", learningOutcomes: ["Analyze ethical considerations", "Form informed opinions"], activities: ["Ethics discussions", "Position papers"], resources: ["Ethics materials"], assessment: "Ethics test" },
          { week: 5, strand: "Advanced Crop Management", topic: "Crop Production Plans", learningOutcomes: ["Design crop production plans", "Plan harvest logistics"], activities: ["Plan development", "Harvest simulation"], resources: ["Planning templates"], assessment: "Planning quiz" },
          { week: 6, strand: "Advanced Crop Management", topic: "Quality and Grading", learningOutcomes: ["Analyze post-harvest management", "Evaluate grading systems"], activities: ["Quality assessment", "Grading practice"], resources: ["Grading guides"], assessment: "Quality test" },
          { week: 7, strand: "Advanced Animal Production", topic: "Breeding Programs", learningOutcomes: ["Analyze breeding programs", "Design selection criteria"], activities: ["Breeding strategy design", "Record analysis"], resources: ["Breeding data"], assessment: "Breeding quiz" },
          { week: 8, strand: "Farm Business", topic: "Business Planning", learningOutcomes: ["Develop farm business plans", "Analyze financial records"], activities: ["Business plan creation", "Financial management"], resources: ["Business templates"], assessment: "Business test" },
          { week: 9, strand: "Food Security", topic: "Global Food Security", learningOutcomes: ["Explain global food security challenges", "Analyze agriculture-nutrition relationship"], activities: ["Food security research", "Solution proposals"], resources: ["Global data"], assessment: "Food security quiz" },
          { week: 10, strand: "Career Pathways", topic: "Careers in Agriculture", learningOutcomes: ["Identify career opportunities", "Describe qualifications needed"], activities: ["Career research", "Guest speakers"], resources: ["Career guides"], assessment: "Career presentation" }
        ]
      },
      term2: { weeks: [] },
      term3: { weeks: [] },
      term4: { weeks: [] }
    }
  };

  // French Junior Agriculture Weekly Lessons for Scheme of Work component
  const juniorAgricultureWeeklyLessonsFrench = {
    year7: {
      term1: {
        weeks: [
          { week: 1, strand: "Introduction à l'Agriculture", topic: "Qu'est-ce que l'agriculture?", learningOutcomes: ["Identifier différents types d'agriculture", "Expliquer l'importance de l'agriculture pour la société"], activities: ["Discussions sur l'impact de l'agriculture", "Présentations de recherche"], resources: ["Manuel", "Images de fermes"], assessment: "Quiz d'introduction" },
          { week: 2, strand: "Introduction à l'Agriculture", topic: "Types d'agriculture", learningOutcomes: ["Identifier l'agriculture végétale et animale", "Décrire le rôle de l'agriculture dans l'alimentation"], activities: ["Visites de fermes", "Création d'affiches"], resources: ["Supports visuels"], assessment: "Test d'identification" },
          { week: 3, strand: "Science des Plantes", topic: "Parties d'une plante", learningOutcomes: ["Identifier les parties d'une plante", "Décrire les fonctions végétales"], activities: ["Dissection de plantes", "Exercices d'étiquetage"], resources: ["Spécimens végétaux"], assessment: "Quiz parties des plantes" },
          { week: 4, strand: "Science des Plantes", topic: "Photosynthèse", learningOutcomes: ["Décrire le processus de photosynthèse", "Expliquer les besoins des plantes"], activities: ["Expériences de photosynthèse", "Culture de plantes"], resources: ["Équipement de laboratoire"], assessment: "Test photosynthèse" },
          { week: 5, strand: "Science du Sol", topic: "Introduction au sol", learningOutcomes: ["Décrire ce qu'est le sol", "Expliquer l'importance pour la croissance des plantes"], activities: ["Échantillonnage de sol", "Profils de sol"], resources: ["Échantillons de sol"], assessment: "Quiz bases du sol" },
          { week: 6, strand: "Science du Sol", topic: "Types de sol", learningOutcomes: ["Identifier différents types de sol", "Décrire les caractéristiques du sol"], activities: ["Tests de texture du sol", "Classification"], resources: ["Différents sols"], assessment: "Test types de sol" },
          { week: 7, strand: "Science Animale", topic: "Animaux de ferme", learningOutcomes: ["Identifier les animaux de ferme communs", "Décrire les besoins de base des animaux"], activities: ["Recherche sur les animaux", "Plans de soins animaux"], resources: ["Images d'animaux"], assessment: "Quiz identification animaux" },
          { week: 8, strand: "Science Animale", topic: "Utilisation des animaux", learningOutcomes: ["Expliquer comment les animaux sont utilisés en agriculture", "Décrire les produits animaux"], activities: ["Observation de ferme", "Analyse des produits"], resources: ["Visite de ferme"], assessment: "Test utilisation animaux" },
          { week: 9, strand: "Agriculture Durable", topic: "Bases de durabilité", learningOutcomes: ["Définir la durabilité en agriculture", "Identifier les pratiques durables"], activities: ["Discussions", "Études de cas"], resources: ["Guides de durabilité"], assessment: "Quiz durabilité" },
          { week: 10, strand: "Sécurité", topic: "Sécurité à la ferme", learningOutcomes: ["Identifier les dangers courants", "Démontrer les pratiques sécuritaires"], activities: ["Audits de sécurité", "Démonstrations de procédures"], resources: ["Équipement de sécurité"], assessment: "Test sécurité" }
        ]
      },
      term2: { weeks: [] },
      term3: { weeks: [] },
      term4: { weeks: [] }
    },
    year8: {
      term1: {
        weeks: [
          { week: 1, strand: "Croissance des Plantes", topic: "Cycle de vie des plantes", learningOutcomes: ["Décrire le cycle de vie des plantes", "Identifier les étapes de croissance"], activities: ["Observer les étapes de croissance", "Diagrammes du cycle de vie"], resources: ["Graines", "Tableaux de croissance"], assessment: "Quiz cycle de vie" },
          { week: 2, strand: "Croissance des Plantes", topic: "Facteurs de croissance", learningOutcomes: ["Expliquer les facteurs affectant la croissance", "Investiguer les facteurs environnementaux"], activities: ["Expériences de croissance", "Collecte de données"], resources: ["Matériel de laboratoire"], assessment: "Test facteurs" },
          { week: 3, strand: "Production de Cultures", topic: "Méthodes de production", learningOutcomes: ["Décrire différentes méthodes de production", "Expliquer la rotation des cultures"], activities: ["Visites de terrain", "Comparaison des méthodes"], resources: ["Visites de fermes"], assessment: "Quiz méthodes" },
          { week: 4, strand: "Production de Cultures", topic: "Cultures régionales", learningOutcomes: ["Identifier les cultures principales de la région", "Décrire l'agriculture locale"], activities: ["Recherche sur les cultures", "Exercices de planification"], resources: ["Guides des cultures locales"], assessment: "Test cultures régionales" },
          { week: 5, strand: "Gestion du Sol", topic: "Composition du sol", learningOutcomes: ["Expliquer la composition et structure du sol", "Décrire les méthodes de test du sol"], activities: ["Travaux pratiques de test du sol", "Analyse de composition"], resources: ["Kits de test"], assessment: "Quiz composition" },
          { week: 6, strand: "Gestion du Sol", topic: "Améliorer la fertilité", learningOutcomes: ["Identifier les moyens d'améliorer la fertilité", "Appliquer les méthodes d'amélioration"], activities: ["Création de compost", "Expériences d'amendement"], resources: ["Matériaux de compostage"], assessment: "Test fertilité" }
        ]
      },
      term2: { weeks: [] },
      term3: { weeks: [] },
      term4: { weeks: [] }
    },
    year9: {
      term1: {
        weeks: [
          { week: 1, strand: "Science des Plantes Avancée", topic: "Génétique des plantes", learningOutcomes: ["Comprendre la génétique et sélection des plantes", "Expliquer les processus de pollinisation"], activities: ["Exercices carré de Punnett", "Études génétiques"], resources: ["Matériel végétal"], assessment: "Quiz génétique" },
          { week: 2, strand: "Science des Plantes Avancée", topic: "Méthodes de propagation", learningOutcomes: ["Pratiquer semis, boutures, greffage", "Démontrer les techniques de greffage"], activities: ["Pratique de greffage", "Analyse de rendement"], resources: ["Outils de greffage"], assessment: "Test propagation" },
          { week: 3, strand: "Gestion des Ravageurs", topic: "Stratégies LIR", learningOutcomes: ["Identifier les stratégies de lutte intégrée", "Appliquer les principes de LIR"], activities: ["Planification LIR", "Protocoles de sécurité"], resources: ["Guides LIR"], assessment: "Quiz LIR" },
          { week: 4, strand: "Gestion des Ravageurs", topic: "Contrôle biologique", learningOutcomes: ["Décrire les méthodes de contrôle biologique", "Évaluer les méthodes de contrôle"], activities: ["Recherche pesticides naturels", "Pratique d'application"], resources: ["Matériaux de contrôle"], assessment: "Test bio-contrôle" }
        ]
      },
      term2: { weeks: [] },
      term3: { weeks: [] },
      term4: { weeks: [] }
    },
    year10: {
      term1: {
        weeks: [
          { week: 1, strand: "Systèmes Agricoles", topic: "Systèmes mondiaux", learningOutcomes: ["Analyser différents systèmes agricoles", "Évaluer l'efficacité et durabilité"], activities: ["Comparaisons mondiales", "Projets d'efficacité"], resources: ["Données agriculture mondiale"], assessment: "Quiz systèmes" },
          { week: 2, strand: "Systèmes Agricoles", topic: "Évaluation de durabilité", learningOutcomes: ["Évaluer la durabilité des pratiques", "Proposer des améliorations"], activities: ["Audits de durabilité", "Plans d'amélioration"], resources: ["Outils d'évaluation"], assessment: "Test évaluation" },
          { week: 3, strand: "Biotechnologie", topic: "Biotech en agriculture", learningOutcomes: ["Expliquer le rôle de la biotechnologie", "Comprendre la modification génétique"], activities: ["Débats OGM", "Projets de recherche"], resources: ["Guides biotech"], assessment: "Quiz biotech" },
          { week: 4, strand: "Biotechnologie", topic: "Considérations éthiques", learningOutcomes: ["Analyser les considérations éthiques", "Débattre des implications"], activities: ["Discussions éthiques", "Documents de position"], resources: ["Matériaux éthiques"], assessment: "Test éthique" }
        ]
      },
      term2: { weeks: [] },
      term3: { weeks: [] },
      term4: { weeks: [] }
    }
  };

  // Get weekly lessons for the selected year (with French support)
  const getJuniorWeeklyLessons = () => {
    if (isFrench) {
      return juniorAgricultureWeeklyLessonsFrench;
    }
    return juniorAgricultureWeeklyLessons;
  };

  // Get unit tests formatted for Junior years (for TermExamGenerator)
  const getJuniorUnitTestsForYear = () => {
    const juniorData = getJuniorCurriculumData();
    const unitTestLabel = isFrench ? "Test Unitaire" : "Unit Test";
    const durationLabel = isFrench ? "45 minutes" : "45 minutes";
    return juniorData.topics.map((topic, idx) => ({
      id: `junior-test-${idx + 1}`,
      title: `${topic.name} - ${unitTestLabel}`,
      duration: durationLabel,
      totalMarks: 50,
      sections: [
        { name: isFrench ? "Choix Multiples" : "Multiple Choice", questions: 10, marks: 20 },
        { name: isFrench ? "Réponse Courte" : "Short Answer", questions: 5, marks: 20 },
        { name: isFrench ? "Application Pratique" : "Practical Application", questions: 2, marks: 10 }
      ],
      sampleQuestions: topic.slos.slice(0, 3)
    }));
  };

  // Print Junior Curriculum function
  const printJuniorCurriculum = () => {
    const juniorData = getJuniorCurriculumData();
    const printWindow = window.open('', '_blank');
    
    const titleText = isFrench ? `Programme d'Agriculture - ${selectedYear}` : `${selectedYear} Agriculture Curriculum`;
    const strandText = isFrench ? "Thème" : "Strand";
    const topicText = isFrench ? "Sujet" : "Topic";
    const sloText = isFrench ? "Objectifs d'Apprentissage (OAA)" : "Student Learning Outcomes";
    const activityText = isFrench ? "Activités Suggérées" : "Suggested Activities";
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${titleText}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #16a34a; }
          h2 { color: #374151; border-bottom: 2px solid #16a34a; padding-bottom: 5px; margin-top: 30px; }
          .topic { margin-bottom: 25px; padding: 15px; background: #f8fafc; border-left: 4px solid #16a34a; }
          .topic-title { font-weight: bold; font-size: 16px; color: #16a34a; }
          .slos { margin-top: 10px; }
          .slo { margin: 5px 0 5px 20px; }
          .activities { margin-top: 10px; padding-top: 10px; border-top: 1px solid #e5e7eb; }
          .activity { margin: 5px 0 5px 20px; color: #6b7280; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>${titleText}</h1>
        <h2>${strandText}: ${juniorData.strand}</h2>
        ${juniorData.topics.map((topic, idx) => `
          <div class="topic">
            <div class="topic-title">${topicText} ${idx + 1}: ${topic.name}</div>
            <div class="slos">
              <strong>${sloText}:</strong>
              ${topic.slos.map(slo => `<div class="slo">• ${slo}</div>`).join('')}
            </div>
            ${topic.activities && topic.activities.length > 0 ? `
              <div class="activities">
                <strong>${activityText}:</strong>
                ${topic.activities.map(act => `<div class="activity">• ${act}</div>`).join('')}
              </div>
            ` : ''}
          </div>
        `).join('')}
        <div style="text-align: center; margin-top: 40px; color: #666; font-size: 12px;">
          ${isFrench ? 'Généré par le Portail Éducatif de Penama' : 'Generated from Penama Education Portal'} | ${new Date().toLocaleDateString()}
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  // Print Junior Scheme of Work function
  const printJuniorSchemeOfWork = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const weeklyLessonsData = getJuniorWeeklyLessons();
    const weeklyData = weeklyLessonsData[yearKey]?.term1?.weeks || [];
    const printWindow = window.open('', '_blank');
    
    const titleText = isFrench ? `Agriculture ${selectedYear} - Plan de Travail Terme 1` : `${selectedYear} Agriculture - Term 1 Scheme of Work`;
    const headers = isFrench 
      ? { week: "Semaine", strand: "Thème", topic: "Sujet", outcomes: "Objectifs d'Apprentissage", activities: "Activités", assessment: "Évaluation" }
      : { week: "Week", strand: "Strand", topic: "Topic", outcomes: "Learning Outcomes", activities: "Activities", assessment: "Assessment" };
    
    const content = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${titleText}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; font-size: 11px; }
          h1 { text-align: center; color: #16a34a; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background: #16a34a; color: white; }
          tr:nth-child(even) { background: #f9f9f9; }
          .strand { color: #16a34a; font-weight: bold; }
          @media print { body { padding: 0; font-size: 10px; } }
        </style>
      </head>
      <body>
        <h1>${titleText}</h1>
        <table>
          <thead>
            <tr>
              <th>${headers.week}</th>
              <th>${headers.strand}</th>
              <th>${headers.topic}</th>
              <th>${headers.outcomes}</th>
              <th>${headers.activities}</th>
              <th>${headers.assessment}</th>
            </tr>
          </thead>
          <tbody>
            ${weeklyData.map(week => `
              <tr>
                <td>${week.week}</td>
                <td class="strand">${week.strand}</td>
                <td>${week.topic}</td>
                <td>${week.learningOutcomes?.join('; ') || ''}</td>
                <td>${week.activities?.join(', ') || ''}</td>
                <td>${week.assessment || ''}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div style="text-align: center; margin-top: 40px; color: #666; font-size: 12px;">
          ${isFrench ? 'Généré par le Portail Éducatif de Penama' : 'Generated from Penama Education Portal'} | ${new Date().toLocaleDateString()}
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printTest = (test) => {
    const printWindow = window.open('', '_blank');
    let content = `
      <html>
      <head>
        <title>${test.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; line-height: 1.6; }
          h1 { text-align: center; color: #16a34a; margin-bottom: 5px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .instructions { margin-bottom: 25px; padding: 10px; border-left: 3px solid #16a34a; background: #f0fdf4; }
          .section { margin-bottom: 25px; page-break-inside: avoid; }
          .section-title { background: #16a34a; color: white; padding: 8px 15px; font-weight: bold; margin-bottom: 15px; }
          .sample-questions { margin-top: 10px; padding-left: 20px; }
          .sample-questions li { margin-bottom: 8px; color: #374151; }
          @media print { body { padding: 20px; } .section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>${test.title}</h1>
        <p class="subtitle">${selectedYear} Agriculture</p>
        
        <div class="info-box">
          <div class="info-row"><span>Duration:</span><span>${test.duration}</span></div>
          <div class="info-row"><span>Total Marks:</span><span>${test.totalMarks}</span></div>
          <div class="info-row"><span>Name:</span><span>____________________</span></div>
          <div class="info-row"><span>Date:</span><span>____________________</span></div>
        </div>
        
        ${test.sections?.map(section => `
          <div class="section">
            <div class="section-title">${section.name} (${section.marks} marks)</div>
            <p>${section.questions} questions</p>
          </div>
        `).join('') || ''}
        
        ${test.sampleQuestions ? `
          <div class="section">
            <div class="section-title">Sample Questions</div>
            <ul class="sample-questions">
              ${test.sampleQuestions.map(q => `<li>${q}</li>`).join('')}
            </ul>
          </div>
        ` : ''}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printCurriculum = () => {
    const printWindow = window.open('', '_blank');
    
    let content = `
      <html>
      <head>
        <title>Agriculture Curriculum - ${selectedYear}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #16a34a; }
          h2 { color: #374151; border-bottom: 2px solid #16a34a; padding-bottom: 5px; margin-top: 30px; }
          h3 { color: #16a34a; }
          .strand { margin-bottom: 30px; page-break-inside: avoid; }
          .topic { margin: 10px 0; padding: 10px; background: #f8fafc; border-left: 3px solid #16a34a; }
          .outcome { margin: 5px 0 5px 20px; }
          @media print { body { padding: 0; } .strand { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>Agriculture Curriculum</h1>
        <h2>${curriculumData.title}</h2>
        <p>${curriculumData.description}</p>
        <p><strong>Hours per Year:</strong> ${curriculumData.hoursPerYear} | <strong>Hours per Week:</strong> ${curriculumData.hoursPerWeek}</p>
        
        ${curriculumData.strands?.map((strand, index) => `
          <div class="strand">
            <h3>Strand ${index + 1}: ${strand.name} (${strand.hours} hours - ${strand.percentage}%)</h3>
            ${strand.topics?.map(topic => `
              <div class="topic">
                <strong>${topic.id}: ${topic.name}</strong>
                <p>${topic.description}</p>
                ${topic.weeks ? `<p><em>Weeks: ${topic.weeks}</em></p>` : ''}
                ${topic.learningOutcomes ? `
                  <div><strong>Learning Outcomes:</strong></div>
                  ${topic.learningOutcomes.map(lo => `<div class="outcome">• ${lo}</div>`).join('')}
                ` : ''}
              </div>
            `).join('')}
          </div>
        `).join('')}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading curriculum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="agriculture-curriculum-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-green-600 to-emerald-500 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Leaf className="h-8 w-8" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Agriculture Curriculum</CardTitle>
                <CardDescription className="text-green-100 mt-1">
                  {isJuniorYear() 
                    ? "Junior Secondary (Years 7-10) - Vanuatu National Syllabus"
                    : "Senior Secondary (Years 11-13) - Vanuatu National Syllabus"}
                </CardDescription>
              </div>
            </div>
            
            {/* Year Selector */}
            <div className="flex items-center gap-3">
              <span className="text-green-100 text-sm">Year Level:</span>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[140px] bg-white/20 border-white/30 text-white" data-testid="agriculture-year-selector">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {teacherYears.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Junior Year Syllabus Card */}
      {isJuniorYear() && (
        <Card className="border-2 border-green-200 bg-green-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-green-700">
              <FileText className="h-5 w-5" />
              Junior Agriculture Syllabus (Years 7-10)
            </CardTitle>
            <CardDescription>
              Access the official Vanuatu National Agriculture syllabus for junior secondary years.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-white rounded-lg border">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Download className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">{isFrench ? "Guide de l'enseignant Agriculture Y7-10" : "Y7-10 Agriculture Teacher Guide"}</p>
                  <p className="text-sm text-gray-500">{isFrench ? "Document complet du programme (Excel)" : "Complete curriculum document (Excel)"}</p>
                </div>
              </div>
              <Button 
                onClick={() => window.open(isFrench 
                  ? 'https://customer-assets.emergentagent.com/job_970640de-cef7-456f-9d19-6bc4a1ec460a/artifacts/jvicrd6u_Agriculture_OAA_Guide_Enseignant_FRAN%C3%87AIS.xlsx'
                  : 'https://customer-assets.emergentagent.com/job_970640de-cef7-456f-9d19-6bc4a1ec460a/artifacts/k43at5er_Agriculture_SLOs_Complete_Teacher_Guide.xlsx', '_blank')}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="h-4 w-4 mr-2" />
                {isFrench ? "Télécharger" : "Download Syllabus"}
              </Button>
            </div>
            <p className="text-sm text-gray-600">
              {isFrench ? "Utilisez l'onglet Générateur IA pour créer des leçons basées sur les sujets du programme d'agriculture." : "Use the AI Lesson Planner tab to generate lessons based on the junior agriculture syllabus topics."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Course Overview - Only show for Senior years or as general info */}
      {!isJuniorYear() && (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-gray-500">Hours/Year</p>
                <p className="font-semibold">{curriculumData?.hoursPerYear || 120} hours</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-sm text-gray-500">Strands</p>
                <p className="font-semibold">{curriculumData?.strands?.length || 5} Strands</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">Assessment</p>
                <p className="font-semibold">
                  {selectedYear === "Year 11" 
                    ? "IE 60% / CA 40%" 
                    : "EE 60% / IA 40%"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-gray-500">Hours/Week</p>
                <p className="font-semibold">{curriculumData?.hoursPerWeek || 4} hours</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      )}

      {/* Main Content - Same tabbed layout for both Junior and Senior */}
      {isJuniorYear() ? (
        /* Junior Years (7-10) - Full tabbed layout matching Senior */
        <Card>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid grid-cols-8 m-4">
              <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="curriculum-tab">
                <BookOpen className="h-4 w-4" />
                <span className="hidden md:inline">Curriculum</span>
              </TabsTrigger>
              <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="schemes-tab">
                <Clock className="h-4 w-4" />
                <span className="hidden md:inline">Schemes</span>
              </TabsTrigger>
              <TabsTrigger value="tests" className="flex items-center gap-2" data-testid="tests-tab">
                <ClipboardCheck className="h-4 w-4" />
                <span className="hidden md:inline">Unit Tests</span>
              </TabsTrigger>
              <TabsTrigger value="notes" className="flex items-center gap-2" data-testid="notes-tab">
                <FileText className="h-4 w-4" />
                <span className="hidden md:inline">Study Notes</span>
              </TabsTrigger>
              <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="term-exam-tab">
                <FileQuestion className="h-4 w-4" />
                <span className="hidden md:inline">Term Exam</span>
              </TabsTrigger>
              <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100" data-testid="quick-print-tab">
                <Files className="h-4 w-4" />
                <span className="hidden md:inline">Quick Print</span>
              </TabsTrigger>
              <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-green-100" data-testid="lesson-plans-tab">
                <PenTool className="h-4 w-4" />
                <span className="hidden md:inline">Lesson Plans</span>
              </TabsTrigger>
              <TabsTrigger value="ai-lesson" className="flex items-center gap-2 bg-amber-100" data-testid="ai-lesson-tab">
                <Sparkles className="h-4 w-4" />
                <span className="hidden md:inline">AI Generator</span>
              </TabsTrigger>
            </TabsList>

            {/* Curriculum Tab - SLOs & Activities */}
            <TabsContent value="curriculum" className="p-4">
              <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-800">
                      {selectedYear} Agriculture - Student Learning Outcomes
                    </h2>
                    <p className="text-gray-600">
                      Strand: {getJuniorCurriculumData().strand}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={() => window.open(isFrench 
                        ? 'https://customer-assets.emergentagent.com/job_970640de-cef7-456f-9d19-6bc4a1ec460a/artifacts/jvicrd6u_Agriculture_OAA_Guide_Enseignant_FRAN%C3%87AIS.xlsx'
                        : 'https://customer-assets.emergentagent.com/job_970640de-cef7-456f-9d19-6bc4a1ec460a/artifacts/k43at5er_Agriculture_SLOs_Complete_Teacher_Guide.xlsx', '_blank')}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      {isFrench ? "Voir le Guide" : "View Syllabus"}
                    </Button>
                    <Button 
                      onClick={printJuniorCurriculum}
                      variant="outline"
                      className="gap-2"
                    >
                      <Printer className="h-4 w-4" />
                      {isFrench ? "Imprimer" : "Print"}
                    </Button>
                  </div>
                </div>

                {/* Sub-Strands / Topics with SLOs */}
                <Accordion type="multiple" className="space-y-3">
                  {getJuniorCurriculumData().topics.map((topic, index) => (
                    <AccordionItem key={index} value={`topic-${index}`} className="border rounded-lg overflow-hidden">
                      <AccordionTrigger className={`px-4 py-3 bg-green-50 hover:bg-green-100`}>
                        <div className="flex items-center gap-3">
                          {getTopicIcon(index)}
                          <Badge className="bg-green-500 text-white">Topic {index + 1}</Badge>
                          <span className="font-semibold">{topic.name}</span>
                          <span className="text-gray-500 text-sm ml-2">({topic.slos?.length || 0} SLOs)</span>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="p-4 bg-white">
                        <div className="space-y-4">
                          {/* SLOs */}
                          <div>
                            <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                              <Target className="h-4 w-4 text-green-600" />
                              Student Learning Outcomes (SLOs):
                            </h4>
                            <ul className="space-y-2 pl-6">
                              {topic.slos.map((slo, sloIndex) => (
                                <li key={sloIndex} className="flex items-start gap-2 text-sm">
                                  <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{slo}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                          
                          {/* Activities */}
                          {topic.activities && topic.activities.length > 0 && (
                            <div className="mt-3 pt-3 border-t">
                              <h4 className="font-semibold text-sm text-gray-700 mb-2 flex items-center gap-2">
                                <Lightbulb className="h-4 w-4 text-amber-500" />
                                Suggested Activities:
                              </h4>
                              <ul className="space-y-1 pl-6">
                                {topic.activities.map((activity, actIndex) => (
                                  <li key={actIndex} className="flex items-start gap-2 text-sm text-gray-600">
                                    <span className="text-amber-500">•</span>
                                    <span>{activity}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </TabsContent>

            {/* Schemes Tab */}
            <TabsContent value="schemes" className="p-4">
              <SubjectSchemeOfWork
                subject={isFrench ? "Agriculture" : "Agriculture"}
                year={parseInt(selectedYear.replace('Year ', ''))}
                weeklyLessons={getJuniorWeeklyLessons()}
                subjectColor="green"
              />
            </TabsContent>

            {/* Unit Tests Tab */}
            <TabsContent value="tests" className="p-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-700">{isFrench ? `Tests Unitaires - Agriculture ${selectedYear}` : `Unit Tests - ${selectedYear} Agriculture`}</h3>
                    <p className="text-gray-500">Assessment materials aligned with junior agriculture SLOs</p>
                  </div>
                  <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="Select Term" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="term1">Term 1</SelectItem>
                      <SelectItem value="term2">Term 2</SelectItem>
                      <SelectItem value="term3">Term 3</SelectItem>
                      <SelectItem value="term4">Term 4</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Generated Unit Tests based on Topics */}
                <div className="grid gap-4">
                  {getJuniorCurriculumData().topics.slice(0, 3).map((topic, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <ClipboardCheck className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">{topic.name} - Unit Test</p>
                              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                                <span>Duration: 45 minutes</span>
                                <span>•</span>
                                <span>Total: 50 marks</span>
                              </div>
                            </div>
                          </div>
                          <Button 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setActiveTab('ai-lesson');
                              toast.info('Use AI Generator to create a custom test for this topic');
                            }}
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Generate Test
                          </Button>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <Badge variant="secondary" className="text-xs">Multiple Choice: 20 marks</Badge>
                          <Badge variant="secondary" className="text-xs">Short Answer: 20 marks</Badge>
                          <Badge variant="secondary" className="text-xs">Practical: 10 marks</Badge>
                        </div>
                        <div className="mt-3 p-3 bg-gray-50 rounded">
                          <p className="text-sm font-medium text-gray-700 mb-2">Topics Covered:</p>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {topic.slos.slice(0, 3).map((slo, sloIdx) => (
                              <li key={sloIdx}>• {slo}</li>
                            ))}
                          </ul>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Info Card for AI Generation */}
                <Card className="bg-purple-50 border-purple-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Sparkles className="h-6 w-6 text-purple-600" />
                      <div>
                        <p className="font-medium text-purple-800">Generate Custom Assessments</p>
                        <p className="text-sm text-purple-600">Use the AI Generator tab to create tailored unit tests, quizzes, and assessments for your junior agriculture class.</p>
                      </div>
                      <Button 
                        onClick={() => setActiveTab('ai-lesson')} 
                        className="bg-purple-600 hover:bg-purple-700 ml-auto"
                      >
                        Go to AI Generator
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Study Notes Tab */}
            <TabsContent value="notes" className="p-4">
              <div className="space-y-4">
                <div className="mb-4">
                  <h3 className="text-lg font-semibold text-gray-700">Study Notes - {selectedYear} Agriculture</h3>
                  <p className="text-gray-500">Comprehensive study materials organized by topic</p>
                </div>
                
                <div className="grid gap-4">
                  {getJuniorCurriculumData().topics.map((topic, idx) => (
                    <Card key={idx} className="overflow-hidden">
                      <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 pb-2">
                        <div className="flex items-center gap-3">
                          {getTopicIcon(idx)}
                          <div>
                            <CardTitle className="text-lg text-green-800">{topic.name}</CardTitle>
                            <CardDescription>{topic.slos.length} learning outcomes</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="pt-4">
                        <Accordion type="single" collapsible>
                          <AccordionItem value={`notes-${idx}`}>
                            <AccordionTrigger className="text-left">
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4 text-green-600" />
                                <span>View Study Notes</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="prose prose-sm max-w-none p-3 bg-gray-50 rounded-lg">
                                <h4 className="text-green-700 font-bold">Key Concepts</h4>
                                <ul>
                                  {topic.slos.map((slo, sloIdx) => (
                                    <li key={sloIdx} className="text-sm text-gray-700">{slo}</li>
                                  ))}
                                </ul>
                                
                                {topic.activities && topic.activities.length > 0 && (
                                  <>
                                    <h4 className="text-green-700 font-bold mt-4">Learning Activities</h4>
                                    <ul>
                                      {topic.activities.map((activity, actIdx) => (
                                        <li key={actIdx} className="text-sm text-gray-700">{activity}</li>
                                      ))}
                                    </ul>
                                  </>
                                )}
                              </div>
                              
                              <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                                <h5 className="font-medium text-amber-800 mb-2">Study Tips</h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  <li>💡 Review the SLOs before each lesson</li>
                                  <li>💡 Practice the suggested activities with classmates</li>
                                  <li>💡 Connect concepts to local farming practices</li>
                                </ul>
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        </Accordion>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Term Exam Tab */}
            <TabsContent value="term-exam" className="p-4">
              <TermExamGenerator
                subject="Agriculture"
                year={parseInt(selectedYear.replace('Year ', ''))}
                unitTests={getJuniorUnitTestsForYear()}
                subjectColor="green"
              />
            </TabsContent>

            {/* Quick Print Tab */}
            <TabsContent value="quick-print" className="p-4">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Printer className="h-5 w-5 text-blue-600" />
                    Quick Print - {selectedYear} Agriculture
                  </CardTitle>
                  <CardDescription>
                    Print all curriculum resources for your Agriculture class
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div 
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => window.open(isFrench 
                        ? 'https://customer-assets.emergentagent.com/job_970640de-cef7-456f-9d19-6bc4a1ec460a/artifacts/jvicrd6u_Agriculture_OAA_Guide_Enseignant_FRAN%C3%87AIS.xlsx'
                        : 'https://customer-assets.emergentagent.com/job_970640de-cef7-456f-9d19-6bc4a1ec460a/artifacts/k43at5er_Agriculture_SLOs_Complete_Teacher_Guide.xlsx', '_blank')}
                      data-testid="print-syllabus"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <FileText className="h-5 w-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-medium">{isFrench ? "Guide Y7-10" : "Y7-10 Teacher Guide"}</p>
                          <p className="text-sm text-gray-500">{isFrench ? "Document Excel complet" : "Complete Excel document"}</p>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={printJuniorCurriculum}
                      data-testid="print-curriculum"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <BookOpen className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-medium">{isFrench ? "Imprimer Programme & OAA" : "Print Curriculum & SLOs"}</p>
                          <p className="text-sm text-gray-500">{isFrench ? "Tous les objectifs d'apprentissage" : "All learning outcomes"}</p>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={printJuniorSchemeOfWork}
                      data-testid="print-scheme"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                          <Calendar className="h-5 w-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">Print Scheme of Work</p>
                          <p className="text-sm text-gray-500">Weekly planning guide</p>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => setActiveTab('ai-lesson')}
                      data-testid="generate-lesson-plan"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Sparkles className="h-5 w-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-medium">Generate Lesson Plan</p>
                          <p className="text-sm text-gray-500">AI-powered lesson planning</p>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => window.print()}
                      data-testid="print-page"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gray-100 rounded-lg">
                          <Printer className="h-5 w-5 text-gray-600" />
                        </div>
                        <div>
                          <p className="font-medium">Print Current Page</p>
                          <p className="text-sm text-gray-500">{isFrench ? "Imprimer cette page" : "Print this page content"}</p>
                        </div>
                      </div>
                    </div>
                    <div 
                      className="p-4 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => window.open(isFrench 
                        ? 'https://customer-assets.emergentagent.com/job_970640de-cef7-456f-9d19-6bc4a1ec460a/artifacts/jvicrd6u_Agriculture_OAA_Guide_Enseignant_FRAN%C3%87AIS.xlsx'
                        : 'https://customer-assets.emergentagent.com/job_970640de-cef7-456f-9d19-6bc4a1ec460a/artifacts/k43at5er_Agriculture_SLOs_Complete_Teacher_Guide.xlsx', '_blank')}
                      data-testid="download-excel"
                    >
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-emerald-100 rounded-lg">
                          <Download className="h-5 w-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-medium">{isFrench ? "Télécharger Excel" : "Download Excel"}</p>
                          <p className="text-sm text-gray-500">{isFrench ? "Guide complet de l'enseignant" : "Complete Teacher Guide"}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Lesson Plans Tab */}
            <TabsContent value="lesson-plans" className="p-4">
              <LessonPlanGenerator 
                defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
                subject="Agriculture"
                subjectColor="green"
                allowedYears={['7', '8', '9', '10']}
                weeklyLessons={getJuniorWeeklyLessons()}
              />
            </TabsContent>

            {/* AI Lesson Plans Tab */}
            <TabsContent value="ai-lesson" className="p-4">
              <AILessonPlanGenerator 
                defaultSubject="agriculture"
                defaultYear={parseInt(selectedYear.replace('Year ', ''))}
                schoolType="secondary"
                allowedYears={teacherYears}
                allowedSubjects={['agriculture']}
              />
            </TabsContent>
          </Tabs>
        </Card>
      ) : (
      /* Senior Years (11-13) - Show full curriculum */
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-6 m-4">
            <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="curriculum-tab">
              <BookOpen className="h-4 w-4" />
              Curriculum
            </TabsTrigger>
            <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="schemes-tab">
              <Clock className="h-4 w-4" />
              Schemes
            </TabsTrigger>
            <TabsTrigger value="tests" className="flex items-center gap-2" data-testid="tests-tab">
              <ClipboardCheck className="h-4 w-4" />
              Unit Tests
            </TabsTrigger>
            <TabsTrigger value="notes" className="flex items-center gap-2" data-testid="notes-tab">
              <FileText className="h-4 w-4" />
              Study Notes
            </TabsTrigger>
            <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="term-exam-tab">
              <FileQuestion className="h-4 w-4" />
              Term Exam
            </TabsTrigger>
            <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100" data-testid="quick-print-tab">
              <Files className="h-4 w-4" />
              Quick Print
            </TabsTrigger>
            <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-green-100" data-testid="lesson-plans-tab">
              <PenTool className="h-4 w-4" />
              Lesson Plans
            </TabsTrigger>
            <TabsTrigger value="ai-lesson" className="flex items-center gap-2 bg-amber-100" data-testid="ai-lesson-tab">
              <Sparkles className="h-4 w-4" />
              AI Generator
            </TabsTrigger>
          </TabsList>

          {/* Schemes Tab - Weekly Scheme of Work */}
          <TabsContent value="schemes" className="p-4">
            <SubjectSchemeOfWork
              subject="Agriculture"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={agricultureWeeklyLessons}
              subjectColor="green"
            />
          </TabsContent>

          {/* Term Exam Tab */}
          <TabsContent value="term-exam" className="p-4">
            <TermExamGenerator
              subject="Agriculture"
              year={parseInt(selectedYear.replace('Year ', ''))}
              unitTests={getUnitTestsForYear()}
              subjectColor="green"
            />
          </TabsContent>

          {/* Quick Print Tab */}
          <TabsContent value="quick-print" className="p-4">
            <QuickPrintAll
              subject="Agriculture"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={agricultureWeeklyLessons}
              subjectColor="green"
            />
          </TabsContent>

          {/* Lesson Plans Tab - Full Week Mon-Fri */}
          <TabsContent value="lesson-plans" className="p-4">
            <LessonPlanGenerator 
              defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
              subject="Agriculture"
              subjectColor="green"
              allowedYears={['11', '12', '13']}
              weeklyLessons={agricultureWeeklyLessons}
            />
          </TabsContent>

          {/* Curriculum Tab */}
          <TabsContent value="curriculum" className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">{curriculumData?.title || `${selectedYear} Agriculture`}</h3>
                <p className="text-gray-500">{curriculumData?.description || 'Understanding agricultural systems and practices'}</p>
              </div>
              <Button variant="outline" onClick={printCurriculum}>
                <Printer className="h-4 w-4 mr-2" />
                Print Curriculum
              </Button>
            </div>
            
            <Accordion type="multiple" className="space-y-3">
              {curriculumData?.strands?.map((strand, index) => {
                const StrandIcon = strandIcons[index] || Leaf;
                const colorClass = strandColors[index] || "green";
                return (
                  <AccordionItem key={strand.id} value={strand.id} className="border rounded-lg overflow-hidden">
                    <AccordionTrigger className={`px-4 py-3 bg-${colorClass}-50 hover:bg-${colorClass}-100`}>
                      <div className="flex items-center gap-3">
                        <StrandIcon className={`h-5 w-5 text-${colorClass}-600`} />
                        <Badge className={`bg-${colorClass}-500 text-white`}>
                          Strand {index + 1}
                        </Badge>
                        <span className="font-semibold">{strand.name}</span>
                        <span className="text-gray-500 text-sm ml-2">({strand.hours} hours - {strand.percentage}%)</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-4 bg-white">
                      <div className="space-y-3">
                        {strand.topics?.map((topic, tIdx) => (
                          <div key={tIdx} className="p-3 bg-gray-50 rounded-lg border-l-4 border-l-green-400">
                            <div className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">{topic.id}</Badge>
                                  <p className="font-medium">{topic.name}</p>
                                </div>
                                <p className="text-sm text-gray-500 mb-2">{topic.description}</p>
                                {topic.weeks && <Badge variant="outline" className="mr-2">Weeks {topic.weeks}</Badge>}
                                
                                {topic.learningOutcomes && topic.learningOutcomes.length > 0 && (
                                  <div className="mt-3 p-3 bg-white rounded border">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Learning Outcomes:</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                      {topic.learningOutcomes.slice(0, 5).map((outcome, oIdx) => (
                                        <li key={oIdx} className="flex items-start gap-2">
                                          <span className="text-green-500">•</span>
                                          {outcome}
                                        </li>
                                      ))}
                                      {topic.learningOutcomes.length > 5 && (
                                        <li className="text-green-600 font-medium">
                                          ... and {topic.learningOutcomes.length - 5} more outcomes
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </TabsContent>

          {/* Unit Tests Tab */}
          <TabsContent value="tests" className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Unit Tests - {selectedYear}</h3>
                <p className="text-gray-500">Assessment materials and practical evaluations</p>
              </div>
              <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Select Term" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="term1">Term 1</SelectItem>
                  <SelectItem value="term2">Term 2</SelectItem>
                  <SelectItem value="term3">Term 3</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {unitTests[selectedTerm] && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700">{unitTests[selectedTerm].title}</h4>
                <div className="grid gap-4">
                  {unitTests[selectedTerm].tests?.map((test, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <ClipboardCheck className="h-5 w-5 text-green-600" />
                            </div>
                            <div>
                              <p className="font-medium">{test.title}</p>
                              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                                <span>Duration: {test.duration}</span>
                                <span>•</span>
                                <span>Total: {test.totalMarks} marks</span>
                              </div>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => printTest(test)}>
                            <Printer className="h-4 w-4 mr-2" />
                            Print
                          </Button>
                        </div>
                        
                        {test.sections && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {test.sections.map((section, sIdx) => (
                              <Badge key={sIdx} variant="secondary" className="text-xs">
                                {section.name}: {section.marks} marks
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {test.sampleQuestions && (
                          <div className="mt-3 p-3 bg-gray-50 rounded">
                            <p className="text-sm font-medium text-gray-700 mb-2">Sample Questions:</p>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {test.sampleQuestions.slice(0, 3).map((q, qIdx) => (
                                <li key={qIdx}>• {q}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Practical Assessments */}
            {unitTests.practicalAssessments && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-4">Practical Assessments</h4>
                <div className="grid gap-4">
                  {unitTests.practicalAssessments.map((practical, idx) => (
                    <Card key={idx} className="border-l-4 border-l-amber-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <Tractor className="h-5 w-5 text-amber-600" />
                          </div>
                          <div>
                            <p className="font-medium">{practical.title}</p>
                            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                              <span>Duration: {practical.duration}</span>
                              <span>•</span>
                              <span>{practical.marks} marks</span>
                            </div>
                            {practical.skills && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {practical.skills.map((skill, sIdx) => (
                                  <Badge key={sIdx} variant="outline" className="text-xs">{skill}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Study Notes Tab */}
          <TabsContent value="notes" className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Study Notes - {selectedYear}</h3>
              <p className="text-gray-500">Comprehensive study materials organized by strand</p>
            </div>
            
            <div className="grid gap-4">
              {Object.entries(studentNotes).map(([strandKey, strand]) => (
                <Card key={strandKey} className="overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 pb-2">
                    <div className="flex items-center gap-3">
                      {strand.image && (
                        <img 
                          src={strand.image} 
                          alt={strand.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <CardTitle className="text-lg text-green-800">{strand.title}</CardTitle>
                        <CardDescription>{strand.overview}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <Accordion type="single" collapsible>
                      {strand.units?.map((unit, uIdx) => (
                        <AccordionItem key={uIdx} value={unit.id}>
                          <AccordionTrigger className="text-left">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-green-600" />
                              <span>{unit.title}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="prose prose-sm max-w-none p-3 bg-gray-50 rounded-lg">
                              <div 
                                className="whitespace-pre-wrap text-sm"
                                dangerouslySetInnerHTML={{ 
                                  __html: unit.content
                                    .replace(/## /g, '<h3 class="text-green-700 font-bold mt-4 mb-2">')
                                    .replace(/### /g, '<h4 class="text-green-600 font-semibold mt-3 mb-1">')
                                    .replace(/#### /g, '<h5 class="font-medium mt-2">')
                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/\n/g, '<br/>')
                                }}
                              />
                            </div>
                            
                            {unit.keyTerms && unit.keyTerms.length > 0 && (
                              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                                <h5 className="font-medium text-green-800 mb-2">Key Terms</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {unit.keyTerms.map((term, tIdx) => (
                                    <div key={tIdx} className="text-sm">
                                      <span className="font-medium text-green-700">{term.term}:</span>{' '}
                                      <span className="text-gray-600">{term.definition}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {unit.practiceQuestions && unit.practiceQuestions.length > 0 && (
                              <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                                <h5 className="font-medium text-amber-800 mb-2">Practice Questions</h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {unit.practiceQuestions.map((q, qIdx) => (
                                    <li key={qIdx}>• {q}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {unit.examTips && unit.examTips.length > 0 && (
                              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                <h5 className="font-medium text-blue-800 mb-2">Exam Tips</h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {unit.examTips.map((tip, tipIdx) => (
                                    <li key={tipIdx}>💡 {tip}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* AI Lesson Plans Tab */}
          <TabsContent value="ai-lesson" className="p-4">
            <AILessonPlanGenerator 
              defaultSubject="agriculture"
              defaultYear={parseInt(selectedYear.replace('Year ', ''))}
              schoolType="secondary"
              allowedYears={teacherYears}
              allowedSubjects={['agriculture']}
            />
          </TabsContent>
        </Tabs>
      </Card>
      )}
    </div>
  );
}
